/**
 * SOURCE picker for the Control Surfaces workbench: a unified, grouped rack of
 * every CAD source the project can segment -
 *   - "Examples"        the backend-listed example geometries (three sub-groups:
 *                        Aircraft screen / STEP / STL). One-gesture pick.
 *   - "nTop parametric" partner .ntop notebooks (declared scalar inputs +
 *                        optional pre-baked deflection frames). Picking one
 *                        enters the workbench's nTop MODE (no segment call) -
 *                        meta shows the baked frame count, else the file size.
 *   - "Project designs" the project's editable aircraft designs. Picking one
 *                        EXPORTS it to a STEP (slow - shows "Exporting..."), then
 *                        segments the returned path.
 *   - "This session"    ephemeral, tab-only sources (a seed handed from the
 *                        Aircraft screen, a manually-typed path) - badged.
 * Clicking any chip is the one-gesture path into the workbench; the parent wires
 * each kind back onto runSegment (or ntop mode). Renders nothing when every
 * group is empty.
 *
 * Styling follows the ControlSurfaceControls idiom - inline CSSProperties off the
 * V5 tokens + the theme-aware highlift palette, no hardcoded hexes.
 */
import type { CSSProperties } from "react";

import type { ExampleGeometry } from "../../api/controlSurface";
import type { AircraftDesignSummary } from "../../api/aircraft";
import type { NtopNotebook } from "../../api/ntop";
import type { CsSource } from "./csSources";
import type { HighliftPalette } from "../highlift/foil/useHighliftTheme";

const GROUPS: Array<{ id: ExampleGeometry["group"]; title: string }> = [
  { id: "aircraft-screen", title: "Aircraft screen" },
  { id: "step", title: "STEP examples" },
  { id: "stl", title: "STL examples" },
];

function formatSize(sizeMb: number): string {
  if (!Number.isFinite(sizeMb) || sizeMb < 0) return "";
  return sizeMb >= 10 ? `${sizeMb.toFixed(0)} MB` : `${sizeMb.toFixed(1)} MB`;
}

export function ControlSurfaceExamples({
  examples,
  ntopNotebooks,
  ntopError,
  designs,
  designsError,
  sessionSources,
  busy,
  exportingDesignId,
  onPickExample,
  onPickNtop,
  onPickDesign,
  onPickSession,
  palette,
}: {
  examples: ExampleGeometry[];
  /** nTop parametric notebooks (empty when the feature is absent here). */
  ntopNotebooks: NtopNotebook[];
  /** Non-404 ntop list failure (a 404 never reaches here - the group just hides). */
  ntopError: string | null;
  designs: AircraftDesignSummary[];
  designsError: string | null;
  /** Ephemeral, tab-only sources (seed / manual). */
  sessionSources: CsSource[];
  /** True while a segment run is in flight - chips disable so sources cannot race. */
  busy: boolean;
  /** The design currently exporting (shows "Exporting..."), or null. */
  exportingDesignId: string | null;
  onPickExample: (example: ExampleGeometry) => void;
  onPickNtop: (notebook: NtopNotebook) => void;
  onPickDesign: (design: AircraftDesignSummary) => void;
  onPickSession: (source: CsSource) => void;
  palette: HighliftPalette;
}) {
  const hasExamples = examples.length > 0;
  const hasNtop = ntopNotebooks.length > 0 || ntopError !== null;
  const hasDesigns = designs.length > 0 || designsError !== null;
  const hasSession = sessionSources.length > 0;
  if (!hasExamples && !hasNtop && !hasDesigns && !hasSession) return null;

  const S = makeExampleStyles(palette);
  const exporting = exportingDesignId !== null;

  return (
    <div style={S.rack} data-testid="cs-examples-rack">
      {hasExamples
        ? GROUPS.map(({ id, title }) => {
            const members = examples.filter((e) => e.group === id);
            if (members.length === 0) return null;
            return (
              <div key={id} style={S.group}>
                <span style={S.groupHead}>{title}</span>
                <div style={S.chips}>
                  {members.map((ex) => (
                    <button
                      key={ex.path}
                      style={{ ...S.chip, ...(busy ? S.chipBusy : null) }}
                      onClick={() => onPickExample(ex)}
                      disabled={busy}
                      title={ex.path}
                    >
                      <span>{ex.label}</span>
                      <span style={S.chipMeta}>{formatSize(ex.size_mb)}</span>
                      {ex.kind === "stl" ? <span style={S.badge}>STL</span> : null}
                    </button>
                  ))}
                </div>
              </div>
            );
          })
        : null}

      {hasNtop ? (
        <div style={S.group} data-testid="cs-ntop-group">
          <span style={S.groupHead}>nTop parametric</span>
          {ntopNotebooks.length > 0 ? (
            <div style={S.chips}>
              {ntopNotebooks.map((nb) => {
                const frameCount = nb.frames?.frames.length ?? 0;
                return (
                  <button
                    key={nb.key}
                    style={{ ...S.chip, ...(busy ? S.chipBusy : null) }}
                    onClick={() => onPickNtop(nb)}
                    disabled={busy}
                    title={nb.path}
                  >
                    <span>{nb.label}</span>
                    <span style={S.chipMeta}>
                      {frameCount > 0 ? `${frameCount} frames` : formatSize(nb.size_mb)}
                    </span>
                    <span style={S.badge}>nTop</span>
                  </button>
                );
              })}
            </div>
          ) : null}
          {ntopError ? (
            <span style={S.groupNote}>nTop unavailable - {ntopError}</span>
          ) : null}
        </div>
      ) : null}

      {hasDesigns ? (
        <div style={S.group} data-testid="cs-designs-group">
          <span style={S.groupHead}>Project designs</span>
          {designs.length > 0 ? (
            <div style={S.chips}>
              {designs.map((d) => {
                const isExporting = exportingDesignId === d.design_id;
                const disabled = busy || exporting;
                return (
                  <button
                    key={d.design_id}
                    style={{ ...S.chip, ...(disabled ? S.chipBusy : null) }}
                    onClick={() => onPickDesign(d)}
                    disabled={disabled}
                    title={`${d.name} - ${d.ops_count} edit(s), base ${d.base}`}
                  >
                    <span>{isExporting ? "Exporting..." : d.name}</span>
                    {!isExporting ? (
                      <span style={S.chipMeta}>{d.ops_count} ops</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : null}
          {designsError ? (
            <span style={S.groupNote}>Designs unavailable - {designsError}</span>
          ) : null}
        </div>
      ) : null}

      {hasSession ? (
        <div style={S.group} data-testid="cs-session-group">
          <span style={S.groupHead}>This session</span>
          <div style={S.chips}>
            {sessionSources.map((src, i) => (
              <button
                key={`${src.kind === "session" ? src.stepPath ?? src.assemblyId ?? "" : ""}:${i}`}
                style={{ ...S.chip, ...(busy ? S.chipBusy : null) }}
                onClick={() => onPickSession(src)}
                disabled={busy}
                title={src.kind === "session" ? src.stepPath ?? src.assemblyId ?? "" : ""}
              >
                <span>{src.kind === "session" ? src.label : ""}</span>
                <span style={S.badge}>ephemeral</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function makeExampleStyles(palette: HighliftPalette): Record<string, CSSProperties> {
  return {
    rack: { display: "flex", flexDirection: "column", gap: 8 },
    group: { display: "flex", flexDirection: "column", gap: 4 },
    // Small caps matching the panel's fieldLabel idiom (the sectionHead already says "Source").
    groupHead: {
      fontSize: 10.5,
      color: palette.ink4,
      textTransform: "uppercase",
      letterSpacing: 0.4,
    },
    groupNote: { fontSize: 10, color: palette.ink4, lineHeight: 1.5 },
    chips: { display: "flex", flexWrap: "wrap", gap: 6 },
    chip: {
      display: "inline-flex",
      alignItems: "baseline",
      gap: 6,
      padding: "3px 9px",
      borderRadius: 999,
      border: `1px solid ${palette.line}`,
      background: palette.surface,
      color: palette.ink,
      cursor: "pointer",
      font: "11.5px var(--v5-sans, system-ui)",
      textAlign: "left",
    },
    chipBusy: { opacity: 0.55, cursor: "default" },
    chipMeta: { font: "10px var(--v5-mono, monospace)", color: palette.ink4 },
    badge: {
      font: "9px var(--v5-mono, monospace)",
      padding: "1px 4px",
      borderRadius: 3,
      border: `1px solid ${palette.lineStrong}`,
      color: palette.ink3,
      letterSpacing: 0.5,
    },
  };
}
